import { Component, Input, ViewChild, Inject } from '@angular/core';
import { PaymentType, PayCustomerActionResultCode, PayCustomerItemStatus, PayCustomerAction, PayPortionState }
from '../../../../../app/shared/enums/smarthub-enums';
import { PayInfo, PayItemInfo } from '../../../../../app/services/pay-info.model';
import { TranslateService } from '@ngx-translate/core';
import { BankNotificationCode } from '../../../../../app/services/bank-notification-code.model';
import * as _ from 'lodash';
import { PayPortionActionResult, PayPortionActionInfo } from '../../../../../app/services/pay-portion-action-info.model';
import { PayInfoFilters } from '../../pay-info-filters/pay-info-filters.model';
import { DisplayPayItemInfo } from '../pay-info-table.component';
import { FeatureToggleService } from '../../../../shared/services/feature-toggle/feature-toggle.service';

export class PayInfoActionResultsInfo {
  get successfulNumberOfItems(): number {
    return this.successfulItems.length;
  }
  get unsuccessfulNumberOfItems(): number {
    return this.unsuccessfulItems.length;
  }

  get hasSuccessfulCheck(): boolean {
    return _.some(this.successfulItems, item => item.item.paymentType === PaymentType.Check);
  }
  get hasSuccessfulDirectDeposit(): boolean {
    return _.some(this.successfulItems, item => item.item.paymentType === PaymentType.DirectDeposit);
  }

  get isCancelStop() {
    return this.action === PayCustomerAction.CancelStop;
  }

  totalNumberOfItems: number;
  successfulItems: PayItemResultInfo[];
  unsuccessfulItems: PayItemResultInfo[];

  constructor(
    private readonly results: PayPortionActionResult[],
    private readonly pays: DisplayPayItemInfo[],
    public readonly action: PayCustomerAction,
    private readonly confirmationInfo: PayPortionActionInfo[],
    private readonly productConfigService: FeatureToggleService) {
    this.totalNumberOfItems = this.results.length;
    this.successfulItems = [];
    this.unsuccessfulItems = [];
    this._initializePayItemResults();
  }

  private _initializePayItemResults() {
    this.results.forEach(result => {
      const pay = this.pays.find(item =>
                                   item.pay.id === result.payId &&
                                   item.payItem.itemNumber === result.itemNo &&
                                   item.payItem.paymentType === result.type);
      const confirmInfo = this.confirmationInfo.find(info =>
                                                       info.payId === result.payId &&
                                                       info.itemNo === result.itemNo &&
                                                       info.type === result.type);
      if (pay && confirmInfo) {
        const resultInfo = new PayItemResultInfo(
          pay.pay,
          pay.payItem,
          pay.displayName,
          this.action,
          result,
          confirmInfo.reasonCode,
          this.productConfigService);

        if (resultInfo.resultCode === PayCustomerActionResultCode.Success) {
          this.successfulItems.push(resultInfo);
        } else {
          this.unsuccessfulItems.push(resultInfo);
        }
      }
    });
  }
}

class PayItemResultInfo {
  public resultCode: PayCustomerActionResultCode;

  constructor(
    public pay: PayInfo,
    public item: PayItemInfo,
    public displayName: string,
    public action: PayCustomerAction,
    public result: PayPortionActionResult,
    public reasonCode: string,
    private productConfigService: FeatureToggleService) {
    this.resultCode = result.resultCode;
  }

  get errorKey() {
    switch (this.resultCode) {
      case PayCustomerActionResultCode.PastNachaReversalPeriod:
        return this.productConfigService.isFeatureEnabled('achReversalCutOffESTTimeZone')
        ? 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_PAST_NACHA_REVERSAL_PERIOD' :
          'EMPLOYEE_PAYS.STOP_ACTION_RESULT_PAST_NACHA_REVERSAL_PERIOD_PST';
      case PayCustomerActionResultCode.DissallowedUndoStopPaymentRequest:
        return 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_DISALLOWED_UNDO_STOP_PAYMENT';
      case PayCustomerActionResultCode.Ignored:
        return 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_IGNORED';
      case PayCustomerActionResultCode.CollectionVoided:
        return 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_COLLECTION_VOIDED';
      case PayCustomerActionResultCode.CollectionReturned:
        return 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_COLLECTION_RETURNED';
      case PayCustomerActionResultCode.CheckAlreadyPosted:
        return 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_CHECK_POSTED';
      case PayCustomerActionResultCode.CannotStopZeroAmountPayPortion:
        return 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_CANNOT_STOP_ZERO_AMOUNT';
      case PayCustomerActionResultCode.InvalidActionForStatus:
        if (this.action === PayCustomerAction.Stop) {
          const errorKey = this._getStopErrorKeys();
          if (errorKey !== '') {
            return errorKey;
          }
        } else if (this.result.portionState === PayPortionState.Unmodified) {
          return 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_INVALID_ACTION_UN_CANCEL_UNMODIFIED';
        }
        return this._getDefaultError();
      case PayCustomerActionResultCode.NotFoundOrInsufficientAccessRights:
          return 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_INSUFFICIENT_RIGHTS';
      case PayCustomerActionResultCode.GeneralFailure:
      default:
        return this._getDefaultError();
      case PayCustomerActionResultCode.Success:
        return '';
    }
  }

  private _getStopErrorKeys() {
    switch (this.result.portionState) {
      case PayPortionState.CanceledBeforePaid:
      case PayPortionState.CanceledAfterPaid:
      case PayPortionState.CancellationRequested:
        return 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_INVALID_ACTION_CANCELED';
      case PayPortionState.Returned:
        return 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_INVALID_ACTION_RETURNED';
      case PayPortionState.Rejected:
        return 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_INVALID_ACTION_REJECTED';
      case PayPortionState.Deleted:
        return 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_INVALID_ACTION_DELETED';
      case PayPortionState.Reversed:
        return 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_INVALID_ACTION_REVERSED';
      default:
        return '';
    }
  }

  private _getDefaultError() {
    if (this.action === PayCustomerAction.Stop) {
      return 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_DEFAULT_STOP';
    } else {
      return 'EMPLOYEE_PAYS.STOP_ACTION_RESULT_DEFAULT_CANCEL_STOP';
    }
  }
}

@Component({
  selector: 'ssg-pay-info-action-results',
  templateUrl: './pay-info-action-results.component.html',
  styleUrls: ['./pay-info-action-results.component.less']
})
export class PayInfoActionResultsComponent {

  @ViewChild('actionsResultsTemplate', { static: true }) actionResultsTemplate;

  @Input()
  resultsInfo: PayInfoActionResultsInfo;

  successfulColumns = [
    'displayName',
    'pay.payUtcDate',
    'item.paymentType',
    'item.amount',
    'statusText',
    'reasonCode',
  ];

  unsuccessfulColumns = [
    'displayName',
    'item.amount',
    'statusText',
    'resultCode',
  ];

  constructor(
    @Inject(TranslateService) private readonly _translateService: TranslateService) {
  }

  getPaymentTypeTextKey(type: PaymentType): string {
    switch (type) {
      case PaymentType.Check:
        return 'EMPLOYEE_PAYS.PAYMENT_TYPE_CHECK';
      case PaymentType.DirectDeposit:
        return 'EMPLOYEE_PAYS.PAYMENT_TYPE_DIRECT_DEPOSIT';
      default:
        return 'EMPLOYEE_PAYS.PAYMENT_TYPE_UNKNOWN';
    }
  }

  isStatusError(status: PayCustomerItemStatus): boolean {
    switch (status) {
      case PayCustomerItemStatus.PayReturned:
      case PayCustomerItemStatus.PayWithCannotCollectFlag:
      case PayCustomerItemStatus.PayWithCannotPayFlag:
      case PayCustomerItemStatus.PayDeleted:
      case PayCustomerItemStatus.PayReversed:
      case PayCustomerItemStatus.PayRejected:
      case PayCustomerItemStatus.PayCanceledAfterPaid:
      case PayCustomerItemStatus.PayCanceledBeforePaid:
      case PayCustomerItemStatus.PayStopRequested:
      case PayCustomerItemStatus.PayStopInProgress:
        return true;
      case PayCustomerItemStatus.Unknown:
      default:
        return false;
    }
  }

  isStatusWarning(status: PayCustomerItemStatus): boolean {
    switch (status) {
      case PayCustomerItemStatus.CollectionReturned:
        return true;
      default:
        return false;
    }
  }

  getStatusTextKey(status: PayCustomerItemStatus): string {
    return PayInfoFilters.getStatusTextKey(status);
  }

  getStatusDescriptionKey(status: PayCustomerItemStatus): string {
    return PayInfoFilters.getStatusDescriptionKey(status);
  }

  translateBankNotificationCode(bankNotificationCode: BankNotificationCode): string {
    const key = `BANK_NOTIFICATION_CODE.${bankNotificationCode}`;
    return this._translateService.instant(key);
  }

  translateError(result: PayItemResultInfo) {
    return this._translateService.instant(result.errorKey);
  }
}
